import { Component, OnInit } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { isTeamsPartner } from '@app/core/interfaces/enterprise-local';
import { EnterpriseOptionType } from '@app/core/interfaces/enterprise-option-type';
import { SecurityPermissionName } from '@app/core/models/permission';
import { UserLevel } from '@app/core/models/user-level';
import { MenuService } from '@app/menu/services/menu.service';
import { RouterService } from '@app/routing/router.service';
import { License } from '@app/shared/models/license';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { EnterpriseAuthorizationService } from '@app/shared/services/enterprise-authorization.service';
import { RouteDetectionService } from '@app/shared/services/route-detection.service';
import { hasEnterpriseLicense } from '@app/utils/has-enterprise-license';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'portal-enterprise-main-nav',
  templateUrl: './enterprise-main-nav.component.html',
  styles: [],
  standalone: false,
})
export class EnterpriseMainNavComponent implements OnInit {
  public readonly licenses$ = this.enterpriseContextService.enterprise.data$.pipe(
    map((enterprise) => {
      return {
        callCenter: hasEnterpriseLicense(enterprise.c5, License.CallCenter),
        conference: hasEnterpriseLicense(enterprise.c5, License.Conference),
        ivr: hasEnterpriseLicense(enterprise.c5, License.IVR),
        // Any Teams partner is considered as Operator Connect for now and does not allow trunks
        hasTeamsPartner: isTeamsPartner(enterprise.local),
      };
    }),
    startWith({
      callCenter: false,
      conference: false,
      ivr: false,
      hasTeamsPartner: false,
    }),
    untilDestroyed(this)
  );

  // Enable Trunks menu only if trunkfor non Teams-OC domains
  private hasTrunksOptions$ = this.enterpriseAuthorizationService
    .hasAnyEnterpriseOption$([
      EnterpriseOptionType.BusinessTrunkChannels,
      EnterpriseOptionType.MicrosoftTeamsTrunkChannels,
    ])
    .pipe(untilDestroyed(this));
  public readonly showTrunks$ = combineLatest([this.licenses$, this.hasTrunksOptions$]).pipe(
    map(([licenses, hasTrunksOptions]) => {
      // console.log('Found trunk licenses and options', licenses, hasTrunksOptions);
      return (
        hasTrunksOptions &&
        !licenses.hasTeamsPartner &&
        this.authenticationService.hasPermission(this.Permission.VIEW_TRUNKS)
      );
    })
  );

  EnterpriseOption = EnterpriseOptionType;
  Permission = SecurityPermissionName;

  public isActive = {
    acd: false,
    callCenter: false,
    endUser: false,
    site: false,
  };

  public showEnterpriseSwitcher: boolean;

  constructor(
    private authenticationService: AuthenticationService,
    private enterpriseContextService: EnterpriseContextService,
    private enterpriseAuthorizationService: EnterpriseAuthorizationService,
    private menuService: MenuService,
    private routeDetectionService: RouteDetectionService,
    private routerService: RouterService
  ) {
    this.showEnterpriseSwitcher = this.authenticationService.userMinimalLevel(UserLevel.ServiceProviderAdmin);
  }

  public ngOnInit() {
    this.menuService.menuData$.pipe(untilDestroyed(this)).subscribe((state) => {
      this.isActive.acd = this.routeDetectionService.isAcdRoute(this.routerService.currentRouteData.url);
      this.isActive.callCenter = this.routeDetectionService.isCallCenterQueueRoute(
        this.routerService.currentRouteData.url
      );
      this.isActive.endUser = state.activeUserLevels.isEndUser;
      this.isActive.site = this.routeDetectionService.isSiteRoute(this.routerService.currentRouteData.url);
    });
  }
}
